import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Check, ShoppingCart, ChevronRight } from "react-feather";
import {
  selectAllRecipes,
  selectAllIngredients,
  matchRecipesByIngredients,
  cleanIngredientName,
  consolidateIngredients,
  getQuantityString,
  selectUnitSystem,
  shoppingItemKey,
  useT,
  useAppSelector,
} from "@reciperoll/core";
import { RecipeImage } from "../components/RecipeImage";
import { useShoppingListImport } from "../hooks/useShoppingListImport";

// "What can I make?" — enter ingredients you have; we rank your recipes by how few
// extra items you'd need, and let you send the gaps straight to the shopping list.
export default function CookFromPantry() {
  const t = useT();
  const recipes = useAppSelector(selectAllRecipes);
  const ingredients = useAppSelector(selectAllIngredients);
  const unitSystem = useAppSelector(selectUnitSystem);
  const { importItems, choiceModal } = useShoppingListImport();

  const [have, setHave] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [assumeStaples, setAssumeStaples] = useState(true);

  const haveKeys = useMemo(() => new Set(have.map(shoppingItemKey)), [have]);

  // Distinct ingredient names across your recipes → autocomplete vocabulary.
  const allSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const ing of ingredients) {
      if (ing.group || !ing.name?.trim()) continue;
      const clean = cleanIngredientName(ing.name);
      const key = shoppingItemKey(clean);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      names.push(clean);
    }
    return names.sort((a, b) => a.localeCompare(b));
  }, [ingredients]);

  const suggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    return allSuggestions
      .filter((n) => !haveKeys.has(shoppingItemKey(n)) && n.toLowerCase().includes(q))
      .slice(0, 6);
  }, [allSuggestions, input, haveKeys]);

  const results = useMemo(
    () => matchRecipesByIngredients(have, recipes, ingredients, { assumeStaples }),
    [have, recipes, ingredients, assumeStaples],
  );

  const addHave = (name: string) => {
    const n = name.trim();
    const key = shoppingItemKey(n);
    if (!key || haveKeys.has(key)) {
      setInput("");
      return;
    }
    setHave((h) => [...h, n]);
    setInput("");
  };
  const removeHave = (name: string) => setHave((h) => h.filter((x) => x !== name));

  // Which result rows are expanded to show their full missing-ingredient list.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="font-display text-3xl font-extrabold text-ink mb-1">{t("nav.whatCanIMake")}</h1>
      <p className="text-ink-soft mb-6">
        {t("home.whatCanIMakeSub")}
      </p>

      {/* Ingredient chips + autocomplete input */}
      <div className="relative">
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-line bg-surface p-2.5">
          {have.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1.5 rounded-lg bg-terracotta-wash px-2.5 py-1 text-sm font-semibold text-terracotta-ink"
            >
              {name}
              <button
                type="button"
                onClick={() => removeHave(name)}
                aria-label={t("whatCanIMake.removeIngredient", { name })}
                className="text-terracotta-ink/70 hover:text-terracotta-ink cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addHave(suggestions[0] ?? input);
              } else if (e.key === "Backspace" && !input && have.length) {
                removeHave(have[have.length - 1]);
              }
            }}
            placeholder={have.length ? t("whatCanIMake.addAnother") : t("whatCanIMake.ingredientsPlaceholder")}
            className="flex-1 min-w-[8rem] bg-transparent px-1.5 py-1 text-ink outline-none placeholder:text-ink-faint"
          />
        </div>

        {suggestions.length > 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-xl border border-line bg-surface shadow-lg overflow-hidden">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addHave(s)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm text-ink-soft cursor-pointer select-none">
        <input
          type="checkbox"
          checked={assumeStaples}
          onChange={(e) => setAssumeStaples(e.target.checked)}
          className="accent-terracotta"
        />
        {t("whatCanIMake.assumeStaples")}
      </label>

      {/* Results */}
      <div className="mt-8">
        {have.length === 0 ? (
          <p className="text-ink-faint text-sm">
            {t("whatCanIMake.emptyPrompt")}
          </p>
        ) : results.length === 0 ? (
          <p className="text-ink-faint text-sm">
            {t("whatCanIMake.noMatches")}
          </p>
        ) : (
          <>
            <p className="text-sm font-semibold text-ink-soft mb-3">
              {t("library.recipeCount", { count: results.length })}
            </p>
            <div className="flex flex-col gap-3">
              {results.map(({ recipe, have, staples, missing, usesCount }) => {
                // Only your matches out of the non-staple ingredients — assumed
                // staples aren't counted as "have".
                const total = usesCount + missing.length;
                const isOpen = expanded.has(recipe.id);
                const thumb = (
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-terracotta-wash shrink-0">
                    <RecipeImage recipe={recipe} />
                  </div>
                );

                // Nothing to reveal → the whole card opens the recipe.
                if (missing.length === 0) {
                  return (
                    <Link
                      key={recipe.id}
                      to={`/recipe/${recipe.id}`}
                      className="flex items-center gap-3 rounded-2xl border border-line bg-surface p-3 hover:bg-surface-2 transition-colors"
                    >
                      {thumb}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-ink line-clamp-1">{recipe.title}</p>
                        <p className="mt-0.5 inline-flex items-center gap-1 text-sm font-semibold text-green-700">
                          <Check className="w-4 h-4" /> {t("whatCanIMake.haveEverything")}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-ink-faint shrink-0" />
                    </Link>
                  );
                }

                // Has gaps → click the card to expand the full missing list + a
                // link to open the recipe. The Add button stays independent.
                return (
                  <div
                    key={recipe.id}
                    onClick={() => toggleExpanded(recipe.id)}
                    className="rounded-2xl border border-line bg-surface p-3 hover:bg-surface-2 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      {thumb}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-ink line-clamp-1">{recipe.title}</p>
                        <p className="mt-0.5 text-sm text-ink-soft">
                          {t("whatCanIMake.haveOfNeed", { have: usesCount, total, missing: missing.length })}
                        </p>
                        {/* Preview stays in the text column so it wraps before the
                            Add button; the button is top-anchored (self-start) so
                            the changing column height doesn't move it. */}
                        {!isOpen && (
                          <p className="mt-1 text-sm text-ink-faint line-clamp-2">
                            {t("whatCanIMake.missingPreview", { list: missing.map((i) => cleanIngredientName(i.name)).join(", ") })}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          importItems(consolidateIngredients(missing));
                        }}
                        className="shrink-0 self-start flex items-center gap-1.5 rounded-lg bg-terracotta hover:bg-terracotta-dark transition-colors px-3 py-2 text-sm font-bold text-white cursor-pointer"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        {t("common.add")}
                      </button>
                    </div>

                    {isOpen && (
                      <div className="mt-3 pl-[76px]">
                        <p className="text-sm font-semibold text-ink-soft mb-1">
                          {t("whatCanIMake.missingCount", { count: missing.length })}
                        </p>
                        <ul className="text-sm text-ink-soft space-y-0.5">
                          {missing.map((i) => {
                            const qty = getQuantityString(i, 1, unitSystem, t);
                            return (
                              <li key={i.id}>
                                • {qty ? `${qty} ` : ""}
                                {cleanIngredientName(i.name)}
                              </li>
                            );
                          })}
                        </ul>

                        {/* The matched ingredients — shown so the "have" count is
                            self-evident (one entry like "capsicum" can cover two
                            recipe lines). */}
                        {have.length > 0 && (
                          <>
                            <p className="text-sm font-semibold text-green-700 mt-3 mb-1">
                              {t("whatCanIMake.youHaveCount", { count: have.length })}
                            </p>
                            <ul className="text-sm text-ink-soft space-y-0.5">
                              {have.map((i) => {
                                const qty = getQuantityString(i, 1, unitSystem, t);
                                return (
                                  <li key={i.id}>
                                    • {qty ? `${qty} ` : ""}
                                    {cleanIngredientName(i.name)}
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        )}

                        {/* Staples assumed on-hand (not part of the count). */}
                        {staples.length > 0 && (
                          <p className="text-sm text-ink-faint mt-2">
                            {t("whatCanIMake.assumedStaples", { list: staples.map(cleanIngredientName).join(", ") })}
                          </p>
                        )}

                        <Link
                          to={`/recipe/${recipe.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 mt-2 text-sm font-bold text-terracotta-ink hover:text-terracotta-dark"
                        >
                          {t("whatCanIMake.viewRecipe")} <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {choiceModal}
    </div>
  );
}
